<!--
  Tooltip — popover de Otera con sticky-bridge. Reemplaza a TooltipLegacy.
  Posicionamiento hand-rolled vía useTooltipPosition. Slots:
    #trigger  — elemento focuseable que dispara el tooltip.
    #content  — contenido del popover.
-->

<script setup>
import { nextTick, onBeforeUnmount, ref, useId, watch } from 'vue';

import { useTooltipPosition } from '@/composables/useTooltipPosition.js';

defineOptions({ name: 'DsTooltip' });

const props = defineProps({
  closeDelay: { type: Number, default: 200 }
});

const emit = defineEmits(['open', 'close']);

const triggerRef = ref(null);
const popoverRef = ref(null);
const open = ref(false);
const tooltipId = useId();
const { pos, recompute } = useTooltipPosition(triggerRef, popoverRef, open);

let openTimer = null;
let closeTimer = null;

function clearOpenTimer() { if (openTimer) { cancelAnimationFrame(openTimer); openTimer = null; } }
function clearCloseTimer() { if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; } }

function show() {
  clearCloseTimer();
  if (open.value) return;
  clearOpenTimer();
  openTimer = requestAnimationFrame(async () => {
    open.value = true;
    emit('open');
    await nextTick();
    recompute();
  });
}

function hide() {
  clearOpenTimer();
  if (!open.value) return;
  clearCloseTimer();
  closeTimer = setTimeout(() => {
    open.value = false;
    emit('close');
  }, props.closeDelay);
}

function dismissImmediate() {
  clearOpenTimer();
  clearCloseTimer();
  if (open.value) {
    open.value = false;
    emit('close');
  }
  triggerRef.value?.focus?.();
}

function onTriggerFocusOut(evt) {
  const next = evt.relatedTarget;
  if (popoverRef.value && next && popoverRef.value.contains(next)) return;
  hide();
}
function onPopoverFocusOut(evt) {
  const next = evt.relatedTarget;
  if (triggerRef.value && next && triggerRef.value.contains(next)) return;
  hide();
}

function onKeyDown(evt) {
  if (evt.key === 'Escape' && open.value) {
    evt.preventDefault();
    dismissImmediate();
  }
}

watch(open, (v) => {
  if (v) document.addEventListener('keydown', onKeyDown);
  else document.removeEventListener('keydown', onKeyDown);
});

onBeforeUnmount(() => {
  clearOpenTimer();
  clearCloseTimer();
  document.removeEventListener('keydown', onKeyDown);
});

defineExpose({ show, hide, dismissImmediate });
</script>

<template>
  <span
    ref="triggerRef"
    class="ds-tooltip-trigger"
    :aria-describedby="open ? tooltipId : undefined"
    @mouseenter="show"
    @mouseleave="hide"
    @focusin="show"
    @focusout="onTriggerFocusOut"
  >
    <slot name="trigger" />
  </span>
  <teleport to="body">
    <div
      v-if="open"
      :id="tooltipId"
      ref="popoverRef"
      role="tooltip"
      class="ds-tooltip"
      :data-placement="pos.placement"
      :style="{ top: `${pos.top}px`, left: `${pos.left}px` }"
      @mouseenter="clearCloseTimer"
      @mouseleave="hide"
      @focusin="clearCloseTimer"
      @focusout="onPopoverFocusOut"
    >
      <slot name="content" />
    </div>
  </teleport>
</template>

<style scoped>
.ds-tooltip-trigger {
  display: inline-flex;
  align-items: center;
}

.ds-tooltip {
  position: absolute;
  z-index: var(--z-tooltip);
  background: var(--bg-canvas);
  color: var(--fg-primary);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  padding: var(--space-3) var(--space-4);
  font-size: var(--text-sm);
  line-height: var(--lh-snug);
  max-width: 28rem;
  pointer-events: auto;
  animation: ds-tooltip-fade-in var(--motion-fast) var(--motion-ease-out);
}

.ds-tooltip::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: var(--space-3);
}

.ds-tooltip[data-placement='bottom']::before {
  top: calc(-1 * var(--space-3));
}

.ds-tooltip[data-placement='top']::before {
  bottom: calc(-1 * var(--space-3));
}

@keyframes ds-tooltip-fade-in {
  from {
  opacity: 0;
  transform: translateY(var(--space-1));
}

  to {
  opacity: 1;
  transform: translateY(0);
}
}

@media (prefers-reduced-motion: reduce) {
  .ds-tooltip {
  animation: none;
}
}
</style>
